import * as React from 'react';

import { code } from '@streamdown/code';
import { type Components, type PluginConfig, Streamdown } from 'streamdown';
import 'streamdown/styles.css';

import {
  ChatMarkdownTable,
  ChatTableViewerContext,
  ChatTableViewerProvider,
} from '@modules/_shared/components/chat/ChatTableViewer';

import { mdComponents } from './chatMarkdownComponents';

// Markdown renderer for assistant messages, backed by Streamdown (the Vercel AI
// SDK renderer used in mako). It handles incomplete-markdown parsing while
// streaming, block memoization, and link/image hardening + sanitization — so we
// no longer hand-roll a marked + dangerouslySetInnerHTML pipeline.
//
// Streamdown styles itself with Tailwind utility classes; Tailwind is enabled
// app-wide (see src/tailwind.css) and scans Streamdown's dist via `@source`, so
// no emotion wrapper is needed. `text-sm` keeps the chat copy compact.
//
// Streamdown v2 ships syntax highlighting as a separate, opt-in plugin: without
// `plugins={{ code }}` code fences render as plain, uncolored text. The `code`
// plugin (from @streamdown/code) lazy-loads Shiki and defaults to the
// github-light/github-dark theme pair, which follows our `.dark` toggle. It
// only emits inline-style tokens (no Tailwind classes), so no extra `@source`
// is needed beyond Streamdown's own dist.
const plugins: PluginConfig = { code };

// Replaces Streamdown's table wrapper so expand opens our dialog instead of
// its z-50 portal. The link and image overrides stay in mdComponents.
const streamdownComponents: Components = {
  ...mdComponents,
  table: ChatMarkdownTable,
};

type Props = {
  content: string;
};

const StreamingMarkdownInner = ({ content }: Props) => {
  const hasViewer = React.useContext(ChatTableViewerContext) != null;
  const markdown = (
    <Streamdown
      className="crm-md text-sm"
      components={streamdownComponents}
      plugins={plugins}
    >
      {content}
    </Streamdown>
  );
  if (hasViewer) {
    return markdown;
  }
  return <ChatTableViewerProvider>{markdown}</ChatTableViewerProvider>;
};

export const StreamingMarkdown = React.memo(StreamingMarkdownInner);
